import { InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import { apiBaseUrl, apiScopes } from '../authConfig';
import type { CustomerTokens } from '../customer/customerAuth';

/** Error carrying the API's envelope ({ code, message }) so screens can show the real reason. */
export class ApiCallError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly detail?: unknown) {
    super(message);
  }
}

let msal: IPublicClientApplication | null = null;
export function bindMsal(instance: IPublicClientApplication) { msal = instance; }

export async function engineerToken(): Promise<string> {
  if (!msal) throw new Error('MSAL not initialised');
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
  if (!account) throw new ApiCallError(401, 'UNAUTHENTICATED', 'Sign in again.');
  try {
    return (await msal.acquireTokenSilent({ scopes: apiScopes, account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      return (await msal.acquireTokenPopup({ scopes: apiScopes, account })).accessToken;
    }
    throw e;
  }
}

/**
 * Calls the Deployment Manager API as the signed-in engineer. When customer tokens are given
 * they travel in X-Customer-* headers for that one request.
 */
export async function api<T>(path: string, init: { method?: string; body?: unknown; customer?: CustomerTokens } = {}): Promise<T> {
  const headers: Record<string, string> = { Authorization: `Bearer ${await engineerToken()}` };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (init.customer?.sharePoint) headers['X-Customer-SharePoint-Token'] = init.customer.sharePoint;
  if (init.customer?.graph) headers['X-Customer-Graph-Token'] = init.customer.graph;
  if (init.customer?.sharePointHost) headers['X-Customer-SharePoint-Host'] = init.customer.sharePointHost;
  if (init.customer?.arm) headers['X-Customer-Arm-Token'] = init.customer.arm;

  let res: Response;
  try {
    res = await fetch(`${apiBaseUrl}/${path.replace(/^\/+/, '')}`, {
      method: init.method ?? (init.body !== undefined ? 'POST' : 'GET'),
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    });
  } catch {
    throw new ApiCallError(0, 'NETWORK', 'The Deployment Manager API could not be reached.');
  }
  const json = await res.json().catch(() => null) as
    | { success: true; data: T }
    | { success: false; error: { code: string; message: string; detail?: unknown } }
    | null;
  if (!res.ok || !json || json.success !== true) {
    const err = json && json.success === false ? json.error : undefined;
    throw new ApiCallError(res.status, err?.code ?? 'HTTP', err?.message ?? `The API returned HTTP ${res.status}.`, err?.detail);
  }
  return json.data;
}
